// Canonical Uniflo vocabulary the clan can rebrand. Two surfaces
// consume this catalog:
//   1. The admin "Clan Language" dialog renders a tabbed table that
//      walks the registry — every registered group is a tab, every
//      registered term is a row in its tab's table.
//   2. The runtime `useClanTerm(key, fallback)` hook reads the
//      override map from publicConfig and falls back to the canonical
//      label kept here, so any term in the catalog can be rebranded
//      anywhere it's consumed.
//
// Hydration / registration. The registry is mutable. Seed entries
// below cover every clan-facing surface today; new surfaces can hook
// in at their own module-load time:
//
//   import { registerClanTerms } from "../../utils/clanLanguage";
//   registerClanTerms("gadgets", [
//     { key: "story_gadget", label: "Story gadget" },
//   ]);
//
// Registrations are idempotent (re-registering the same term key is a
// no-op) and additive — the next time the admin opens Clan Language
// the new term appears under its tab.
//
// CLAN_TERM_GROUPS and CLAN_TERM_DEFINITIONS are exported as live
// array references that the registry mutates in place; consumers that
// hold a reference automatically see late registrations. Sorting by
// the group's `order` field keeps tabs in a stable display sequence
// regardless of registration order.

const groupRegistry = new Map();

/** Live array of registered groups, sorted by `order`. */
export const CLAN_TERM_GROUPS = [];

/** Live array of registered terms across all groups. */
export const CLAN_TERM_DEFINITIONS = [];

function sortGroups() {
  CLAN_TERM_GROUPS.sort((a, b) => a.order - b.order);
}

/**
 * Register (or update) a Clan Language tab.
 *
 *   registerClanLanguageGroup({ key: "gadgets", label: "Gadgets", order: 50 })
 *
 * Subsequent calls with the same key update the label / order rather
 * than duplicating the tab. `order` is a small integer — the seeded
 * groups use 10..80, so a new group can slot between two existing
 * tabs by picking an order in the gap (e.g. 25 to land between
 * Profiles and Content).
 */
export function registerClanLanguageGroup({ key, label, order = 999 } = {}) {
  const normalizedKey = `${key || ""}`.trim().toLowerCase();
  if (!normalizedKey) return null;
  if (groupRegistry.has(normalizedKey)) {
    const entry = groupRegistry.get(normalizedKey);
    if (label) entry.label = `${label}`;
    if (Number.isFinite(Number(order))) entry.order = Number(order);
    sortGroups();
    return entry;
  }
  const entry = {
    key: normalizedKey,
    label: `${label || normalizedKey}`,
    order: Number.isFinite(Number(order)) ? Number(order) : 999,
  };
  groupRegistry.set(normalizedKey, entry);
  CLAN_TERM_GROUPS.push(entry);
  sortGroups();
  return entry;
}

/**
 * Register one or more terms inside a tab.
 *
 *   registerClanTerms("content", [
 *     { key: "post", label: "Post" },
 *     { key: "card", label: "Card" },
 *   ]);
 *
 * Each term needs `key` (canonical lowercase identifier the
 * useClanTerm hook looks up) and `label` (the default display
 * string when the admin hasn't supplied an override). If the group
 * key isn't registered yet this implicitly registers it with the
 * same label and an order of 999 so the tab still appears.
 *
 * Duplicate keys are ignored — first registration wins so static
 * seed data isn't overwritten by a later module's re-registration.
 */
export function registerClanTerms(groupKey, terms = []) {
  const normalizedGroup = `${groupKey || ""}`.trim().toLowerCase();
  if (!normalizedGroup) return;
  if (!groupRegistry.has(normalizedGroup)) {
    registerClanLanguageGroup({
      key: normalizedGroup,
      label: normalizedGroup,
      order: 999,
    });
  }
  const list = Array.isArray(terms) ? terms : [];
  for (const raw of list) {
    if (!raw?.key) continue;
    const normalizedKey = `${raw.key}`.trim().toLowerCase();
    if (!normalizedKey) continue;
    if (CLAN_TERM_DEFINITIONS.some((entry) => entry.key === normalizedKey)) {
      continue;
    }
    CLAN_TERM_DEFINITIONS.push({
      key: normalizedKey,
      group: normalizedGroup,
      label: `${raw.label || normalizedKey}`,
    });
  }
}

// ── Seeded groups (display order) ──────────────────────────────────
registerClanLanguageGroup({ key: "core",     label: "Core",     order: 10 });
registerClanLanguageGroup({ key: "profiles", label: "Profiles", order: 20 });
registerClanLanguageGroup({ key: "content",  label: "Content",  order: 30 });
registerClanLanguageGroup({ key: "linkages", label: "Linkages", order: 40 });
registerClanLanguageGroup({ key: "gadgets",  label: "Gadgets",  order: 50 });
registerClanLanguageGroup({ key: "kits",     label: "Kits",     order: 60 });
registerClanLanguageGroup({ key: "tools",    label: "Tools",    order: 70 });
registerClanLanguageGroup({ key: "admin",    label: "Admin",    order: 80 });

// ── Seeded terms ───────────────────────────────────────────────────
registerClanTerms("core", [
  { key: "uniflo",           label: "Uniflo" },
  { key: "clan",             label: "Clan" },
  { key: "subclan",          label: "Subclan" },
  { key: "subclan_category", label: "Subclan category" },
]);

registerClanTerms("profiles", [
  { key: "member",   label: "Member" },
  { key: "role",     label: "Role" },
  { key: "founder",  label: "Founder" },
  { key: "profile",  label: "Profile" },
  { key: "category", label: "Category" },
  { key: "dynamics", label: "Dynamics" },
  { key: "units",    label: "Units" },
  { key: "bonds",    label: "Bonds" },
]);

registerClanTerms("content", [
  { key: "post",    label: "Post" },
  { key: "card",    label: "Card" },
  { key: "stack",   label: "Stack" },
  { key: "deck",    label: "Deck" },
  { key: "board",   label: "Board" },
  { key: "comment", label: "Comment" },
]);

registerClanTerms("linkages", [
  { key: "linkage",      label: "Linkage" },
  { key: "gmail",        label: "Gmail" },
  { key: "calendar",     label: "Calendar" },
  { key: "whatsapp",     label: "WhatsApp" },
  { key: "cloudflare",   label: "Cloudflare" },
  { key: "notification", label: "Notification" },
]);

registerClanTerms("gadgets", [
  { key: "gadget", label: "Gadget" },
  { key: "wall",   label: "Wall" },
  { key: "map",    label: "Map" },
  { key: "story",  label: "Story" },
  { key: "poll",   label: "Poll" },
  { key: "forum",  label: "Forum" },
]);

registerClanTerms("kits", [
  { key: "kit",       label: "Kit" },
  { key: "event",     label: "Event" },
  { key: "recording", label: "Recording" },
  { key: "meeting",   label: "Meeting" },
  { key: "invite",    label: "Invite" },
]);

registerClanTerms("tools", [
  { key: "tool",         label: "Tool" },
  { key: "announcement", label: "Announcement" },
  { key: "moderation",   label: "Moderation" },
  { key: "report",       label: "Report" },
]);

registerClanTerms("admin", [
  { key: "admin",        label: "Admin" },
  { key: "dashboard",    label: "Dashboard" },
  { key: "settings",     label: "Settings" },
  { key: "permission",   label: "Permission" },
  { key: "subscription", label: "Subscription" },
]);

// ── Resolvers (signatures unchanged from previous version) ─────────

/**
 * Look up the canonical default for a clan term — the string the UI
 * shows when the admin hasn't supplied an override.
 */
export function getClanTermDefault(termKey, fallback = "") {
  const normalizedKey = `${termKey || ""}`.trim().toLowerCase();
  const entry = CLAN_TERM_DEFINITIONS.find((t) => t.key === normalizedKey);
  return entry?.label || `${fallback || ""}`;
}

/**
 * Resolve a clan term against an override map. Empty / blank overrides
 * fall back to the canonical Uniflo label, then to the supplied
 * fallback (useful when the caller passes a hard-coded sentence
 * fragment that contains the term).
 */
export function resolveClanTerm(overrides = {}, termKey, fallback = "") {
  const normalizedKey = `${termKey || ""}`.trim().toLowerCase();
  const overrideValue = `${overrides?.[normalizedKey] || ""}`.trim();
  if (overrideValue) return overrideValue;
  return getClanTermDefault(normalizedKey, fallback);
}

/**
 * Capitalize the first letter of a resolved term — handy when a
 * sentence starts with the override and the admin typed it lower-case.
 */
export function capitalizeClanTerm(value = "") {
  const normalized = `${value || ""}`;
  if (!normalized) return normalized;
  return normalized.charAt(0).toUpperCase() + normalized.slice(1);
}
